import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { TrendingUp, TrendingDown, Users, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface AnalyticsSectionProps {
  isDetailView?: boolean;
  className?: string;
}

interface MetricCardProps {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ReactNode;
}

function MetricCard({ label, value, change, trend, icon }: MetricCardProps) {
  return (
    <div className="flex flex-col gap-2 p-4 rounded-xl border bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <div className="flex items-center justify-center size-8 rounded-lg bg-muted">
          {icon}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-semibold">{value}</span>
        <span className={`flex items-center gap-0.5 text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
          {trend === 'up' ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
          {change}
        </span>
      </div>
    </div>
  );
}

const MOCK_METRICS: MetricCardProps[] = [
  {
    label: 'Total Subscribers',
    value: '1,247',
    change: '+12.5%',
    trend: 'up',
    icon: <Users className="size-4 text-muted-foreground" />,
  },
  {
    label: 'Monthly Revenue',
    value: '$48,920',
    change: '+8.2%',
    trend: 'up',
    icon: <DollarSign className="size-4 text-muted-foreground" />,
  },
  {
    label: 'Churn Rate',
    value: '2.4%',
    change: '-0.3%',
    trend: 'down',
    icon: <TrendingDown className="size-4 text-muted-foreground" />,
  },
  {
    label: 'Avg. Revenue / User',
    value: '$39.23',
    change: '+4.1%',
    trend: 'up',
    icon: <TrendingUp className="size-4 text-muted-foreground" />,
  },
];

interface MonthlyData {
  month: string;
  subscribers: number;
  revenue: string;
  churn: string;
}

const MOCK_MONTHLY_DATA: MonthlyData[] = [
  { month: 'Sep 2025', subscribers: 1102, revenue: '$42,340', churn: '3.1%' },
  { month: 'Oct 2025', subscribers: 1138, revenue: '$43,890', churn: '2.8%' },
  { month: 'Nov 2025', subscribers: 1167, revenue: '$45,120', churn: '2.6%' },
  { month: 'Dec 2025', subscribers: 1195, revenue: '$46,450', churn: '2.5%' },
  { month: 'Jan 2026', subscribers: 1221, revenue: '$47,680', churn: '2.5%' },
  { month: 'Feb 2026', subscribers: 1247, revenue: '$48,920', churn: '2.4%' },
];

export const AnalyticsSection = React.memo<AnalyticsSectionProps>(({ className }) => {
  return (
    <div className="flex flex-col gap-6">
      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {MOCK_METRICS.map((metric) => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </div>

      {/* Monthly Trend Table */}
      <SectionCard title="Monthly Performance" layout="down" className={className}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Month</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Subscribers</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Revenue</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">Churn</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_MONTHLY_DATA.map((row) => (
                <tr key={row.month} className="border-b last:border-0">
                  <td className="py-3 px-4 font-medium">{row.month}</td>
                  <td className="py-3 px-4 text-right">{row.subscribers.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-medium">{row.revenue}</td>
                  <td className="py-3 px-4 text-right">{row.churn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
});

AnalyticsSection.displayName = 'AnalyticsSection';
